import { AxiosError } from 'axios';
import { Module } from 'vuex';
import { User as IUser, IRootState, IAuthState } from '@types';

import { AccessToken } from '@lib/@m7/oauth2-client';
import { OauthClientInstance, ApiConnectorInstance } from '@/Singletons';

import { UserModel } from '@model';

import Env from '@lib/Env';
import EventBus from '@lib/EventBus';
import BugsnagClient from '@/utils/bugsnagClient';

import { flatten } from 'lodash';

const USER_KEY = 'vc.oauth.user';
const METHOD_KEY = 'vc.oauth.method';

const store: Module<IAuthState, IRootState> = {
    state: {
        accessToken: <AccessToken>OauthClientInstance.getAccessToken(),
        user: <UserModel>UserModel.getFromLocalStorage(USER_KEY),
        authMethod: <string>window.localStorage.getItem(METHOD_KEY),
        isAuthPopupVisible: false,
    },

    getters: {
        getAccessToken: (state, getters) => state.accessToken,
        getUser: (state, getters) => state.user,
        getUserIsImpersonated: (state, getters) => state.user?.isImpersonated() ?? false,
        getUserTermsAgreed: (state, getters) => (state.user?.isImpersonated() || state.user?.hasAgreedTerms()) ?? false,
        getAuthMethod: (state, getters) => state.authMethod,
        getHasAccessToken: (state, getters) => !!getters.getAccessToken,
        getHasValidAccessToken: (state, getters) => getters.getHasAccessToken && getters.getAccessToken.isValid(),
        getIsLoggedIn: (state, getters) => getters.getHasValidAccessToken && !!getters.getUser && !!getters.getUser.id,
        getIsAuthPopupVisible: (state, getters) => state.isAuthPopupVisible,
        hasRole:
            (state, getters) =>
            (...reqRoles: string[]) => {
                return (
                    flatten(reqRoles).filter((role: string) => getters.getUser?.roles?.includes(role)).length ===
                    flatten(reqRoles).length
                );
            },
        hasAnyRole:
            (state, getters) =>
            (...requiredRoles: string[]) => {
                for (let role of flatten(requiredRoles)) {
                    if (getters.hasRole(role)) {
                        return true;
                    }
                }
                return false;
            },
        hasPermissionTo:
            (state, getters) =>
            (...requiredPerms: string[]) => {
                // Exact
                let hasPermission =
                    flatten(requiredPerms).filter((item) => getters.getUser?.permissions?.includes(item) ?? false)
                        .length === flatten(requiredPerms).length;

                if (hasPermission) {
                    return true;
                } else {
                    // Check with wildcards
                    hasPermission = flatten(requiredPerms).reduce((result: boolean, permission) => {
                        return (result =
                            result &&
                            getters.getUser?.permissions.filter((item: string) =>
                                new RegExp('^' + item.replace(/\*/g, '.*') + '$').test(permission)
                            ).length > 0);
                    }, true);
                    return hasPermission;
                }
            },

        hasAnyPermissionTo:
            (state, getters) =>
            (...requiredPerms: string[]) => {
                for (let permission of flatten(requiredPerms)) {
                    if (getters.hasPermissionTo(permission)) {
                        return true;
                    }
                }
                return false;
            },
    },

    mutations: {
        setAccessToken: (state, accessToken: AccessToken) => (state.accessToken = accessToken),
        setUser(state, user: UserModel) {
            user && user.saveToLocalStorage(USER_KEY);
            state.user = user;
        },
        setAuthMethod: (state, method: string) => {
            method && window.localStorage.setItem(METHOD_KEY, method);
            state.authMethod = method;
        },
        openAuthPopup: (state) => (state.isAuthPopupVisible = true),
        closeAuthPopup: (state) => (state.isAuthPopupVisible = false),
    },

    actions: {
        doSilentAuthorization({ commit, dispatch }): Promise<AccessToken> {
            return new Promise((resolve, reject) => {
                OauthClientInstance.silentAuthorization()
                    .then((accessToken: AccessToken) => {
                        commit('setAccessToken', accessToken);
                        commit('setAuthMethod', 'implicit');

                        dispatch('loadMainData')
                            .then((user: UserModel) => {
                                EventBus.$emit('auth.login.success');
                                resolve(accessToken);
                            })
                            .catch((err) => {
                                reject(err);
                            });
                    })
                    .catch((err) => {
                        if (err == 'need_login') {
                            return reject(err);
                        }
                        EventBus.$emit('auth.login.error', err);
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        openImplicitWindow({ commit, dispatch }): Promise<AccessToken> {
            return new Promise((resolve, reject) => {
                OauthClientInstance.openImplicitWindow()
                    .then((accessToken: AccessToken) => {
                        commit('setAccessToken', accessToken);
                        commit('setAuthMethod', 'implicit');

                        dispatch('loadMainData')
                            .then((user: UserModel) => {
                                EventBus.$emit('auth.login.success');
                                resolve(accessToken);
                            })
                            .catch((err) => {
                                reject(err);
                            });
                    })
                    .catch((err) => {
                        if (err == 'need_login') return reject(err);
                        EventBus.$emit('auth.login.error', err);
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        login({ commit, dispatch }, { grant_type, code, username, password }): Promise<any> {
            return new Promise((resolve, reject) => {
                ApiConnectorInstance.login({ grant_type, code, username, password })
                    .then((accessToken: AccessToken) => {
                        commit('setAccessToken', accessToken);
                        commit('setAuthMethod', grant_type);
                        dispatch('loadMainData')
                            .then((user: UserModel) => {
                                EventBus.$emit('auth.login.success');
                                resolve(accessToken);
                            })
                            .catch((err) => {
                                reject(err);
                            });
                    })
                    .catch((err) => {
                        EventBus.$emit('auth.login.error', err);
                        reject(err);
                    });
            });
        },

        whoAmI({ commit }): Promise<UserModel> {
            const include = [
                'actor.lang',
                'actor.personalData',
                'actor.rating',
                'actor.photos',
                'actor.portfolio',
                'actor.nominations',
                'actor.nominations.project',
                'actor.nominations.project.client',
                'actor.nominations.projectType',
                'actor.nominations.contract',
                'actor.locations',
                'actor.locations.type',
                'roles',
                'permissions',
                'directPermissions',
                'impersonatedBy',
            ];

            return new Promise((resolve, reject) => {
                ApiConnectorInstance.whoAmI({ include })
                    .then((user: IUser) => {
                        const userModel = new UserModel(user);
                        BugsnagClient.setUser(userModel.id, userModel.email, `${userModel.name} ${userModel.surname}`);
                        commit('setUser', userModel);
                        commit('setActor', userModel.actor);
                        resolve(userModel);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadMainData({ dispatch }): Promise<any> {
            return dispatch('whoAmI');
        },

        agreeTerms({ dispatch }) {
            return new Promise((resolve, reject) => {
                ApiConnectorInstance.getHttpClient('local')
                    .post('users/agree-terms')
                    .then(() => {
                        dispatch('whoAmI')
                            .then(() => {
                                resolve(null);
                            })
                            .catch((err) => {
                                console.error(err);
                                reject(err);
                            });
                    })
                    .catch((err: AxiosError) => {
                        console.error(err);
                        reject(err);
                    });
            });
        },

        requestPasswordlessLoginCode(context, { channel, recipient }): Promise<any> {
            return ApiConnectorInstance.requestPasswordlessLoginCode({ channel, recipient });
        },

        logout({ dispatch, getters }): Promise<any>[] {
            dispatch('clearAuthData');

            const promises = [ApiConnectorInstance.logout()];

            if (getters.getAuthMethod == 'implicit') {
                promises.push(OauthClientInstance.silentLogout());
            }

            return promises;
        },

        clearAuthData({ dispatch }) {
            dispatch('clearAccessToken');
            dispatch('clearUser');
            // dispatch('clearAuthMethod');
        },

        clearAccessToken({ commit, getters }) {
            getters.getAccessToken && getters.getAccessToken.removeFromLocalStorage();
            commit('setAccessToken', null);
        },

        clearUser({ commit }) {
            window.localStorage.removeItem(USER_KEY);
            commit('setUser', null);
        },

        clearAuthMethod({ commit }) {
            window.localStorage.removeItem(METHOD_KEY);
            commit('setAuthMethod', null);
        },

        openAuthPopup({ commit }) {
            commit('closeAllPopups');
            commit('openAuthPopup');
        },

        closeAuthPopup({ commit }) {
            commit('closeAuthPopup');
        },
    },
};

export default store;
